import "./term-screen";

import { Storybook } from "@tento-ui/ui/storybook";
import { TermScreen } from "./term-screen";
import { TermRow } from "./term-row";

// Every frame below is REAL output: rows captured verbatim from tsh's
// harness-components scene and a live claude lane on rwc-01 (August 2026),
// with the escape byte spelled \u001b because a literal ESC survives no diff
// tool. A storybook is where a component's claims are checked, and this
// component's claim is fidelity to captured bytes — so the bytes are captured,
// not composed.

const CLAUDE_FRAME: string[] = [
  "  \u001b[38;2;52;56;70m────────────────────────────────────────────────────────────────────────────\u001b[0m  ",
  "                                                                                ",
  "  \u001b[1;38;2;215;119;87m✳\u001b[0m \u001b[1;38;2;192;202;245mThinking\u001b[0m                                                   \u001b[38;2;86;95;137mclaude.thinking\u001b[0m  ",
  "  \u001b[38;2;86;95;137m  ⎿ \u001b[0m\u001b[38;2;139;143;163mCaptured sparkle cycle, verb, elapsed time, tokens, interrupt hint.\u001b[0m       ",
  "  \u001b[38;2;215;119;87m✳ Thinking…\u001b[0m\u001b[38;2;125;125;125m (12s · ↑ 1.6k tokens · esc to interrupt)\u001b[0m                          ",
  "                                                                                ",
  "  \u001b[38;2;52;56;70m────────────────────────────────────────────────────────────────────────────\u001b[0m  ",
  "                                                                                ",
  "  \u001b[1;38;2;215;135;135m◼\u001b[0m \u001b[1;38;2;192;202;245mTodo list\u001b[0m                                                 \u001b[38;2;86;95;137mclaude.todo-list\u001b[0m  ",
  "  \u001b[38;2;86;95;137m  ⎿ \u001b[0m\u001b[38;2;139;143;163mCompleted, active, and pending task grammar.\u001b[0m                              ",
  "  \u001b[38;2;135;215;135m  ⎿ ✔ \u001b[0m\u001b[9;38;2;148;148;148mInspect adapter\u001b[0m                                                         ",
  "  \u001b[1;38;2;215;135;135m    ◼ \u001b[0m\u001b[1;38;2;192;202;245mPreserve updates\u001b[0m                                                        ",
  "  \u001b[38;2;148;148;148m    ◻ \u001b[0m\u001b[38;2;192;202;245mVerify snapshots\u001b[0m                                                        ",
  "                                                                                ",
  "  \u001b[38;2;52;56;70m────────────────────────────────────────────────────────────────────────────\u001b[0m  ",
  "                                                                                ",
  "  \u001b[1;38;2;78;169;111m⏺\u001b[0m \u001b[1;38;2;192;202;245mTool call\u001b[0m                                                 \u001b[38;2;86;95;137mclaude.tool-call\u001b[0m  ",
  "  \u001b[38;2;86;95;137m  ⎿ \u001b[0m\u001b[38;2;139;143;163mStatus glyph, tool argument, result disclosure.\u001b[0m                           ",
  "  \u001b[38;2;78;169;111m⏺ \u001b[0m\u001b[38;2;192;202;245mRead\u001b[0m\u001b[38;2;86;95;137m(\u001b[0m\u001b[38;2;125;207;255msrc/main.rs\u001b[0m\u001b[38;2;86;95;137m)\u001b[0m                                                           ",
];

const DIFF_ROWS: string[] = [
  "\u001b[48;2;27;17;19m  \u001b[0m\u001b[38;2;247;118;142;48;2;27;17;19m 12 - \u001b[0m\u001b[38;2;192;202;245;48;2;27;17;19mflatten(update)\u001b[0m\u001b[48;2;27;17;19m                                                       \u001b[0m\u001b[48;2;27;17;19m  \u001b[0m",
  "\u001b[48;2;16;27;20m  \u001b[0m\u001b[38;2;78;169;111;48;2;16;27;20m 12 + \u001b[0m\u001b[38;2;192;202;245;48;2;16;27;20mretain(update)\u001b[0m\u001b[48;2;16;27;20m                                                        \u001b[0m\u001b[48;2;16;27;20m  \u001b[0m",
];

function $terminal(vm: TermScreen.ViewModel): HTMLElement {
  const $screen = TermScreen.makeWith(vm);
  $screen.assignStyles({
    fontFamily: "ui-monospace, monospace",
    fontSize: "12px",
    lineHeight: "1.5",
    padding: "16px",
    background: "#0b0e14",
    color: "#c0caf5",
    borderRadius: "8px",
    overflowX: "auto",
  });
  return $screen;
}

export class TermScreenStoryBook extends Storybook.Default {
  title(): string {
    return "Term Screen";
  }

  examples(): Storybook.Example[] {
    return [
      {
        // The shipped claude components — thinking, todo list, tool call —
        // exactly as the harness-components scene rendered them: truecolour
        // ink, bold markers, struck-through completed entries.
        title: "A captured harness frame",
        render: () => $terminal({ lines: CLAUDE_FRAME }),
      },
      {
        // Two rows whose ground runs the full width: the row-level band
        // hoist. If these render as stripes with leading showing through,
        // the hoist regressed.
        title: "Diff rows are solid bands",
        render: () => $terminal({ lines: DIFF_ROWS }),
      },
      {
        // The same rows through a caller-owned element — the painter is
        // usable without the component, which is how the session replay and
        // any chat block build their rows.
        title: "Painted into a caller's element",
        render: () => {
          const $host = document.createElement("div");
          for (const raw of DIFF_ROWS) {
            $host.appendChild(TermRow.$row(TermRow.parse(raw)));
          }
          $host.style.cssText =
            "font-family:ui-monospace,monospace;font-size:12px;line-height:1.5;" +
            "white-space:pre;padding:16px;background:#0b0e14;color:#c0caf5;border-radius:8px";
          return $host;
        },
      },
      {
        // The degenerate input: nothing to paint, and nothing thrown.
        title: "Empty frame",
        render: () => $terminal({ lines: [] }),
      },
    ];
  }
}
